import {subWeeks} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import OccurrencesDetailsService from "../../../services/reports/occurrences-details-service.js";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const initialFilters = ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    condition: [],
    nature: [],
    neighborhood: [],
    cityFact: [],
    onlyMyCity: true,
    archivedOccurrences: true,
    isFlagrant: false,
    tableMode: false,
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    condition: "Condição dos indivíduos",
    nature: "Natureza",
    neighborhood: "Bairro",
    cityFact: "Município do fato",
    onlyMyCity: "Apenas ocorrências da minha corporação",
    archivedOccurrences: "Contabilizar ocorrências arquivadas",
    isFlagrant: "Flagrante",
    tableMode: "Modo tabela",
}

export default function OccurrencesDetails() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);

    const title = "Detalhamento de ocorrências com indivíduo";
    const [viewMode, setViewMode] = useState("viewer");

    const { data: boName } = useQuery({
        queryKey: ['bo-name'],
        queryFn: () => bosService.getBoName(),
        initialData: "BO",
    });

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 60, "days");

        if (!periodValidation.ok) {
            ReportUtils.alertErrorMessage(periodValidation.message);
            setIsLoading(false);
            return;
        }

        try {
            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                condition: filters.condition?.map(i => i.value) ?? [],
                nature: filters.nature?.map(i => i.value) ?? [],
                neighborhood: filters.neighborhood?.map(i => i.value) ?? [],
                cityFact: filters.cityFact?.map(i => i.value) ?? [],
                onlyMyCity: filters.onlyMyCity ?? false,
                archivedOccurrences: filters.archivedOccurrences ?? false,
                isFlagrant: filters.isFlagrant ?? false,
                tableMode: filters.tableMode ?? false,
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, formattedFilters, true);

            const response = await OccurrencesDetailsService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);
            setAppliedFilters(filters);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply, viewMode !== "editor");

    const safeArray = (value) => Array.isArray(value) ? value : [];

    const hasPositiveQty = (array = []) =>
        safeArray(array).some(item => Number(item?.qty ?? 0) > 0);

    const allOccurrencesHasData = safeArray(reportData?.allOccurrences).some(
        item => Number(item?.totaloccurrences ?? 0) > 0
    );

    const indProfileHasData = safeArray(reportData?.indProfile).some(
        item =>
            Number(item?.total ?? 0) > 0 ||
            Number(item?.reccurrent ?? 0) > 0
    );

    const tempAnalysisHasData = safeArray(reportData?.temp_analysis).some(
        item => Number(item?.qty ?? 0) > 0
    );

    const tableModeHasData = safeArray(reportData?.TableMode).length > 0;

    const hasData =
        !!reportData &&
        (
            allOccurrencesHasData ||
            hasPositiveQty(reportData?.occurrencesNatures) ||
            hasPositiveQty(reportData?.presentedBy) ||
            hasPositiveQty(reportData?.occurrencesNeighborhood) ||
            hasPositiveQty(reportData?.individualAge) ||
            hasPositiveQty(reportData?.individualNationality) ||
            indProfileHasData ||
            tempAnalysisHasData ||
            tableModeHasData
        );

    const noDataValue = hasData ? 1 : 0;

    const cardClassName = [
        "w-full h-fit mr-2 print:!mt-[-15px] transform",
        !reportData || !hasData ? "mt-[59px] !bg-[#f3f3f4] !border-none !-translate-y-[5px]" : "mt-[61px]",
        collapsed ? "ml-2" : "",
    ].join(" ");

    const cardBodyClassName = [
        "flex-1",
        !reportData || !hasData ? "p-[0]" : "px-[20px] py-[5px]",
    ].join(" ");

    const tableMode = !!appliedFilters.tableMode;

    const exportOn = tableMode ? true : tableMode;

    const content = (
        <div className="flex">
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>{title}</Template.Title>
                    <HeaderButtons
                        exportable={exportOn}
                        exportInSheets={true}
                        printable={true}
                        headerChange={true}
                        printComponentRef={printComponentRef}
                        reportData={reportData}
                        data={noDataValue}
                        headerSelected={headerSelected}
                        setHeaderSelected={setHeaderSelected}
                    />
                </Template.Header>
                <Template.Body className={`${collapsed ? "p-[0]" : ""}`}>
                    <Menu buttonRef={buttonRef} fixedTop={fixedTop} collapsed={collapsed} toggleFilters={toggleFilters} clearSearch={clearSearch} apply={apply}>

                        <Filters filters={filters} setFilters={setFilters}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>
            <Card className={cardClassName}>
                <Card.Body className={cardBodyClassName}>
                    <NotAppliedContent reportData={reportData}/>
                    <NoDataContent reportData={reportData} data={noDataValue}/>

                    {reportData && hasData && (
                        <Content
                            reportData={reportData}
                            data={hasData}
                            entity={headerSelected}
                            title={title}
                            collapsed={collapsed}
                            printComponentRef={printComponentRef}
                            setIsLoading={setIsLoading}
                            boName={boName}
                            tableMode={tableMode}
                            setMode={setViewMode}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>


    );

    return (
        <>
            {tableMode ? (
                <SubScroll dependency={[collapsed, reportData]}>
                    {content}
                </SubScroll>
            ) : content}
        </>
    )
}